import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { AppShell } from './components/app-shell'
import {
  AuthenticationBoundary,
  AuthCallback,
  AuthStart,
} from './components/auth-flow'
import { LoadingState } from './components/ui'
import { CliAuthorizationPage } from './pages/cli-authorization-page'

const AccountPage = lazy(() =>
  import('./pages/account-page').then((module) => ({ default: module.AccountPage }))
)
const AdminPage = lazy(() =>
  import('./pages/admin-page').then((module) => ({ default: module.AdminPage }))
)
const DeploymentsPage = lazy(() =>
  import('./pages/deployments-page').then((module) => ({
    default: module.DeploymentsPage,
  }))
)
const DocsPage = lazy(() =>
  import('./pages/docs-page').then((module) => ({ default: module.DocsPage }))
)
const ProjectsPage = lazy(() =>
  import('./pages/projects-page').then((module) => ({ default: module.ProjectsPage }))
)
const UsagePage = lazy(() =>
  import('./pages/usage-page').then((module) => ({ default: module.UsagePage }))
)
const ProjectLayout = lazy(() =>
  import('./pages/project/project-layout').then((module) => ({
    default: module.ProjectLayout,
  }))
)
const ProjectOverview = lazy(() =>
  import('./pages/project/project-overview').then((module) => ({
    default: module.ProjectOverview,
  }))
)
const ProjectDeployments = lazy(() =>
  import('./pages/project/project-deployments').then((module) => ({
    default: module.ProjectDeployments,
  }))
)
const ProjectRepository = lazy(() =>
  import('./pages/project/project-repository').then((module) => ({
    default: module.ProjectRepository,
  }))
)
const ProjectPreviews = lazy(() =>
  import('./pages/project/project-previews').then((module) => ({
    default: module.ProjectPreviews,
  }))
)
const ProjectDomains = lazy(() =>
  import('./pages/project/project-domains').then((module) => ({
    default: module.ProjectDomains,
  }))
)
const ProjectEnvironment = lazy(() =>
  import('./pages/project/project-environment').then((module) => ({
    default: module.ProjectEnvironment,
  }))
)
const ProjectGrant = lazy(() =>
  import('./pages/project/project-grant').then((module) => ({
    default: module.ProjectGrant,
  }))
)
const ProjectLogs = lazy(() =>
  import('./pages/project/project-logs').then((module) => ({
    default: module.ProjectLogs,
  }))
)
const ProjectSettings = lazy(() =>
  import('./pages/project/project-settings').then((module) => ({
    default: module.ProjectSettings,
  }))
)

export default function App() {
  return (
    <Routes>
      <Route path='/auth/start' element={<AuthStart />} />
      <Route path='/auth/callback' element={<AuthCallback />} />
      <Route
        path='/*'
        element={
          <AuthenticationBoundary>
            <AppShell>
              <Suspense fallback={<LoadingState />}>
                <AuthenticatedRoutes />
              </Suspense>
            </AppShell>
          </AuthenticationBoundary>
        }
      />
    </Routes>
  )
}

function AuthenticatedRoutes() {
  return (
    <Routes>
      <Route path='/' element={<Navigate replace to='/projects' />} />
      <Route path='/projects' element={<ProjectsPage />} />
      <Route path='/projects/:projectId' element={<ProjectLayout />}>
        <Route index element={<ProjectOverview />} />
        <Route path='deployments' element={<ProjectDeployments />} />
        <Route path='repository' element={<ProjectRepository />} />
        <Route path='previews' element={<ProjectPreviews />} />
        <Route path='domains' element={<ProjectDomains />} />
        <Route path='environment' element={<ProjectEnvironment />} />
        <Route path='logs' element={<ProjectLogs />} />
        <Route path='ai-grant' element={<ProjectGrant />} />
        <Route path='settings' element={<ProjectSettings />} />
      </Route>
      <Route path='/deployments' element={<DeploymentsPage />} />
      <Route path='/usage' element={<UsagePage />} />
      <Route path='/docs' element={<DocsPage />} />
      <Route path='/account' element={<AccountPage />} />
      <Route path='/cli/authorize' element={<CliAuthorizationPage />} />
      <Route path='/admin' element={<Navigate replace to='/admin/overview' />} />
      <Route path='/admin/:section' element={<AdminPage />} />
      <Route path='*' element={<Navigate replace to='/projects' />} />
    </Routes>
  )
}
